import useTranslation from "@babackapp/translations/use-translation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import type { ScopeFormValues } from "@/lib/override-rules";

export type CarrierOption = {
  id: string;
  name: string;
  pricings: {
    id: string;
    origin?: string | null;
    destination?: string | null;
    price?: number | null;
    currency?: string | null;
  }[];
};

interface ScopeSelectorProps {
  value: ScopeFormValues;
  onChange: (value: ScopeFormValues) => void;
  carriers: CarrierOption[];
  disabled?: boolean;
}

const SCOPE_LEVELS = ["GLOBAL", "CARRIER", "TARIF"] as const;
type ScopeLevel = (typeof SCOPE_LEVELS)[number];

export function ScopeSelector({
  value,
  onChange,
  carriers,
  disabled,
}: ScopeSelectorProps) {
  const t = useTranslation();

  const level: ScopeLevel = value?.level ?? "GLOBAL";

  const selectedCarrier = carriers.find((c) => c.id === value?.carrierId);

  const handleLevelChange = (newLevel: ScopeLevel) => {
    if (newLevel === "GLOBAL") {
      onChange({ level: "GLOBAL", carrierId: null, pricingId: null });
    } else if (newLevel === "CARRIER") {
      onChange({
        level: "CARRIER",
        carrierId: value?.carrierId ?? null,
        pricingId: null,
      });
    } else {
      onChange({
        level: "TARIF",
        carrierId: value?.carrierId ?? null,
        pricingId: value?.pricingId ?? null,
      });
    }
  };

  const handleCarrierChange = (carrierId: string) => {
    onChange({ level, carrierId, pricingId: null });
  };

  const handlePricingChange = (pricingId: string) => {
    onChange({ level, carrierId: value?.carrierId ?? null, pricingId });
  };

  const formatPricingLabel = (pricing: CarrierOption["pricings"][number]) => {
    const parts: string[] = [];
    if (pricing.origin) parts.push(pricing.origin);
    if (pricing.destination) parts.push(`→ ${pricing.destination}`);
    if (pricing.price != null && pricing.currency) {
      parts.push(`(${pricing.price} ${pricing.currency})`);
    }
    return parts.join(" ") || pricing.id;
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select
        value={level}
        onValueChange={(v) => handleLevelChange(v as ScopeLevel)}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 w-44 text-sm bg-white">
          <SelectValue placeholder={t("overrideRules.scope.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {SCOPE_LEVELS.map((l) => (
            <SelectItem key={l} value={l}>
              {t(`overrideRules.scope.${l}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {(level === "CARRIER" || level === "TARIF") && (
        <Select
          value={value?.carrierId ?? ""}
          onValueChange={handleCarrierChange}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 w-44 text-sm bg-white">
            <SelectValue
              placeholder={t("overrideRules.scope.carrierPlaceholder")}
            />
          </SelectTrigger>
          <SelectContent>
            {carriers.map((carrier) => (
              <SelectItem key={carrier.id} value={carrier.id}>
                {carrier.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {level === "TARIF" && selectedCarrier && (
        <Select
          value={value?.pricingId ?? ""}
          onValueChange={handlePricingChange}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 min-w-44 text-sm bg-white">
            <SelectValue
              placeholder={t("overrideRules.scope.tarifPlaceholder")}
            />
          </SelectTrigger>
          <SelectContent>
            {selectedCarrier.pricings.map((pricing) => (
              <SelectItem key={pricing.id} value={pricing.id}>
                {formatPricingLabel(pricing)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
